/*
 * Modal "Prospectar por dirección".
 *
 * Flujo:
 *   1. Buscar dirección en Nominatim → elegir sugerencia.
 *   2. (Opcional) Introducir código del operador y altura estimada.
 *   3. Generar SAR (backend crea prospección + SAR).
 *   4. Aceptar → crea emplazamiento en db.sites.
 *      Descartar → marca la prospección como rechazada.
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { MapPin, Search, Loader2, X, FileText, Check, XCircle, Building2 } from 'lucide-react';
import { SARModal } from './SARButton';
import OperatorSelect from './OperatorSelect';

const AddressSuggest = ({ onPick }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (q.trim().length < 3) return;
    setLoading(true);
    try {
      const { data } = await api.get('/geocode/suggest', { params: { q: q.trim(), limit: 8 } });
      setItems(data || []);
    } catch {
      toast.error('Nominatim no disponible');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
          <input
            data-testid="prospect-addr-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            placeholder="Escribe la dirección, calle o municipio…"
            className="w-full bg-black border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
        </div>
        <button onClick={search} disabled={loading || q.trim().length < 3}
          data-testid="prospect-addr-search"
          className="bg-[#A3FF12] text-black font-black px-4 rounded-lg text-sm hover:bg-[#b8ff3a] disabled:opacity-40">
          {loading ? <Loader2 className="animate-spin" size={14}/> : 'Buscar'}
        </button>
      </div>
      {items.length > 0 && (
        <div className="bg-black/40 border border-white/10 rounded-lg max-h-52 overflow-y-auto divide-y divide-white/5">
          {items.map((it, i) => (
            <button key={i} onClick={() => onPick(it)}
              data-testid={`prospect-addr-option-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-white/5 flex items-start gap-2 text-xs">
              <MapPin size={13} className="text-[#A3FF12] mt-0.5 flex-shrink-0"/>
              <div className="min-w-0">
                <div className="text-white truncate">{it.display_name}</div>
                <div className="text-white/40 text-[10px] mt-0.5">
                  {it.city && `${it.city} · `}{it.province}{it.postcode && ` · ${it.postcode}`}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ProspectByAddressModal = ({ onClose, onSiteCreated }) => {
  const { user } = useAuth();
  const canAccept = user && (user.role === 'admin' || user.role === 'gestor');

  // Steps: 1=pick address, 2=review SAR, 3=accepted
  const [picked, setPicked] = useState(null);
  const [operatorCode, setOperatorCode] = useState('');
  const [operator, setOperator] = useState(null); // {id?, name?, color?, logo_b64?}
  const [heightM, setHeightM] = useState('');
  const [equipmentLocation, setEquipmentLocation] = useState('outdoor');
  const [notes, setNotes] = useState('');
  const [job, setJob] = useState(null); // {job_id, status, progress, stage}
  const [prospection, setProspection] = useState(null);
  const [sar, setSar] = useState(null);
  const [showSar, setShowSar] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const generating = job && ['queued', 'running'].includes(job.status);
  const hasOperator = !!(operator?.id || operator?.name);

  const generate = async () => {
    if (!picked || generating) return;
    if (!hasOperator) {
      toast.error('Elige un operador antes de generar el SAR');
      return;
    }
    try {
      const payload = {
        lat: picked.lat, lng: picked.lng,
        address: picked.display_name,
        height_m: heightM ? Number(heightM) : null,
        site_name: `Prospección · ${picked.display_name.slice(0, 60)}`,
        equipment_location: equipmentLocation,
      };
      if (operator) payload.operator = operator;
      const { data } = await api.post('/research/sar/async', payload);
      setJob({ job_id: data.job_id, status: data.status, progress: 0, stage: data.stage || 'En cola…' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error iniciando el SAR');
    }
  };

  // Poll async SAR job → when done, attach it as a prospection via /from-sar
  useEffect(() => {
    if (!job || !['queued', 'running'].includes(job.status) || !picked) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/research/sar/jobs/${job.job_id}`);
        if (cancelled) return;
        // Preserva job_id (fix contrato: GET devuelve `id`, POST devuelve `job_id`).
        setJob({ ...data, job_id: data.job_id || data.id || job.job_id });
        if (data.status === 'done' && data.sar) {
          try {
            const attach = await api.post('/prospecciones/from-sar', {
              sar_id: data.sar.id,
              address: picked.display_name,
              lat: picked.lat, lng: picked.lng,
              municipality: picked.city || '',
              province: picked.province || '',
              postcode: picked.postcode || '',
              height_m: heightM ? Number(heightM) : null,
              operator_code: operatorCode.trim() || null,
              notes: notes.trim(),
            });
            setProspection(attach.data.prospection);
            setSar(attach.data.sar);
            toast.success('SAR generado. Revísalo antes de aceptar la dirección');
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Error guardando prospección');
          }
        } else if (data.status === 'error') {
          // El watchdog o el runner marcó error → dejamos que el UI muestre el botón "Reintentar"
        }
      } catch { /* transient */ }
    };
    const iv = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [job?.job_id, job?.status]);

  const accept = async () => {
    if (!prospection) return;
    if (!operatorCode.trim() && !prospection.operator_code) {
      toast.error('Introduce el código del operador antes de aceptar');
      return;
    }
    setAccepting(true);
    try {
      const { data } = await api.post(`/prospecciones/${prospection.id}/accept`, {
        operator_code: operatorCode.trim() || null,
      });
      toast.success(`Emplazamiento creado: ${data.site.name}`);
      if (onSiteCreated) onSiteCreated(data.site);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creando el emplazamiento');
    } finally { setAccepting(false); }
  };

  const reject = async () => {
    if (!prospection) return;
    if (!window.confirm('¿Descartar esta prospección? Quedará en el buzón de descartados.')) return;
    setRejecting(true);
    try {
      await api.post(`/prospecciones/${prospection.id}/reject`);
      toast.success('Prospección descartada');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error descartando');
    } finally { setRejecting(false); }
  };

  return (
    <div className="fixed inset-0 z-[10001] bg-black/80 backdrop-blur flex items-center justify-center p-4"
      onClick={onClose} data-testid="prospect-by-address-modal">
      <div onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-white/15 rounded-3xl w-full max-w-2xl max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">PROSPECCIÓN POR DIRECCIÓN</div>
            <h2 className="text-xl font-black text-white mt-1">Analiza una dirección antes de crearla como emplazamiento</h2>
            <p className="text-white/50 text-xs mt-1">Se genera el SAR completo (Adquiriam + BIC + afecciones). Si aceptas, se crea el emplazamiento con el código del operador.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-1" data-testid="close-prospect-modal"><X size={22}/></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Step 1: address */}
          {!prospection && (
            <>
              <AddressSuggest onPick={setPicked}/>
              {picked && (
                <div className="bg-[#A3FF12]/5 border border-[#A3FF12]/30 rounded-xl p-3" data-testid="prospect-picked">
                  <div className="flex items-start gap-2">
                    <MapPin className="text-[#A3FF12] mt-0.5" size={16}/>
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-bold">{picked.display_name}</div>
                      <div className="text-white/50 text-[11px] mt-0.5">
                        {picked.lat.toFixed(5)}, {picked.lng.toFixed(5)}
                        {picked.postcode && ` · CP ${picked.postcode}`}
                      </div>
                    </div>
                    <button onClick={() => setPicked(null)} className="text-white/40 hover:text-white p-1"><X size={14}/></button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-2 mt-3">
                    <label className="block">
                      <span className="text-[10px] uppercase text-white/50 font-bold">Código del operador (opcional)</span>
                      <input value={operatorCode} onChange={(e) => setOperatorCode(e.target.value.toUpperCase())}
                        placeholder="SPA-XX00000 (puedes meterlo después)"
                        data-testid="prospect-operator-code"
                        className="mt-1 w-full bg-black border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
                    </label>
                    <label className="block">
                      <span className="text-[10px] uppercase text-white/50 font-bold">Altura estimada (m)</span>
                      <input type="number" value={heightM} onChange={(e) => setHeightM(e.target.value)}
                        placeholder="p.ej. 25"
                        className="mt-1 w-full bg-black border border-white/10 rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
                    </label>
                  </div>
                  {/* Ubicación de equipos — 3 opciones */}
                  <div className="mt-3" data-testid="prospect-equipment-location">
                    <div className="text-[10px] uppercase text-white/50 font-bold mb-1.5">Ubicación de los equipos</div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {[
                        { id: 'outdoor',          label: 'Outdoor',         desc: 'A la intemperie · cubierta' },
                        { id: 'indoor',           label: 'Indoor',          desc: 'Caseta / hab. a construir' },
                        { id: 'sala_habilitada',  label: 'Sala habilitada', desc: 'Hab. existente del edificio' },
                      ].map((opt) => (
                        <button key={opt.id}
                          type="button"
                          onClick={() => setEquipmentLocation(opt.id)}
                          data-testid={`prospect-eq-loc-${opt.id}`}
                          className={`text-left px-2 py-1.5 rounded-lg border transition-colors ${equipmentLocation === opt.id
                            ? 'bg-[#A3FF12]/10 border-[#A3FF12]/50 text-white'
                            : 'bg-white/5 border-white/10 text-white/60 hover:border-white/25'}`}>
                          <div className="flex items-center gap-1.5">
                            <span className={`w-2.5 h-2.5 rounded-full border-2 ${equipmentLocation === opt.id ? 'border-[#A3FF12] bg-[#A3FF12]' : 'border-white/40'}`}/>
                            <span className="font-black text-[11px]">{opt.label}</span>
                          </div>
                          <div className="text-[9px] text-white/40 mt-0.5 leading-tight">{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="block mt-2">
                    <span className="text-[10px] uppercase text-white/50 font-bold">Notas iniciales (opcional)</span>
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Observaciones para el equipo (accesos, propietario, etc.)"
                      className="mt-1 w-full bg-black border border-white/10 rounded px-2 py-1.5 text-xs text-white outline-none focus:border-[#A3FF12]/50 resize-none"/>
                  </label>
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-black tracking-widest text-[#A3FF12]">OPERADOR *</span>
                      <span className="text-[10px] text-white/50">(obligatorio)</span>
                    </div>
                    <OperatorSelect value={operator} onChange={setOperator}/>
                  </div>
                  <button onClick={generate} disabled={generating || !hasOperator}
                    data-testid="prospect-generate-sar"
                    className="mt-3 w-full bg-[#A3FF12] text-black font-black py-2 rounded-lg text-sm hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center justify-center gap-2">
                    {generating ? <><Loader2 className="animate-spin" size={14}/> Generando SAR… {job.progress || 0}%</> : <><FileText size={14}/> Generar SAR sobre esta dirección</>}
                  </button>
                  {generating && (
                    <div className="mt-2" data-testid="prospect-progress">
                      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-[#A3FF12] transition-all" style={{ width: `${Math.max(3, job.progress || 0)}%` }}/>
                      </div>
                      <div className="text-white/50 text-[10px] mt-1 text-center">{job.stage || 'Trabajando…'}</div>
                    </div>
                  )}
                  {job && job.status === 'error' && (
                    <div className="mt-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="prospect-error">
                      <div className="text-[10px] font-black text-red-300 mb-1">SAR NO COMPLETADO</div>
                      <div className="text-white/80 text-xs mb-2">{job.error || 'La generación no pudo completarse.'}</div>
                      <button onClick={async () => {
                        try {
                          const { data } = await api.post(`/research/sar/jobs/${job.job_id}/retry`);
                          setJob({ job_id: data.job_id, status: data.status, progress: data.progress || 0, stage: data.stage || 'En cola…' });
                        } catch {
                          // Si el backend olvidó el job, arranca uno nuevo
                          setJob(null);
                          setTimeout(generate, 100);
                        }
                      }} data-testid="prospect-retry-btn"
                        className="w-full bg-[#A3FF12] text-black font-black py-1.5 rounded-lg text-xs hover:bg-[#b8ff3a]">
                        Reintentar
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Step 2: review + accept */}
          {prospection && sar && (
            <div className="space-y-3" data-testid="prospect-review">
              <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">SAR GENERADO</div>
                    <div className="text-white font-black text-base mt-1">{sar.code}</div>
                    <div className="text-white/60 text-xs mt-1">{sar.address || prospection.address}</div>
                  </div>
                  <button onClick={() => setShowSar(true)}
                    data-testid="prospect-view-sar"
                    className="bg-white/5 border border-white/10 hover:bg-white/10 text-white font-bold px-3 py-2 rounded-lg text-xs flex items-center gap-1">
                    <FileText size={13}/> Ver SAR
                  </button>
                </div>
                {sar.resumen_ejecutivo && (
                  <div className="mt-3 text-white/80 text-xs italic bg-black/40 rounded p-2 border border-white/5">
                    “{sar.resumen_ejecutivo}”
                  </div>
                )}
              </div>

              <div className="bg-black/40 border border-white/10 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="text-[#A3FF12]" size={16}/>
                  <div className="text-[10px] font-black tracking-widest text-[#A3FF12]">CREAR EMPLAZAMIENTO</div>
                </div>
                <p className="text-white/60 text-xs mb-3">Si aceptas la dirección, se crea un site en estado <b className="text-white">prospección</b> con este código. Puedes cambiarlo más tarde si el operador aún no lo ha comunicado.</p>
                <label className="block">
                  <span className="text-[10px] uppercase text-white/50 font-bold">Código del operador</span>
                  <input value={operatorCode} onChange={(e) => setOperatorCode(e.target.value.toUpperCase())}
                    placeholder={prospection.operator_code || 'SPA-XX00000'}
                    data-testid="prospect-accept-code"
                    className="mt-1 w-full bg-black border border-white/10 rounded px-2 py-2 text-sm text-white outline-none focus:border-[#A3FF12]/50"/>
                </label>
                {canAccept ? (
                  <div className="flex gap-2 mt-3">
                    <button onClick={accept} disabled={accepting || rejecting}
                      data-testid="prospect-accept-btn"
                      className="flex-1 bg-[#A3FF12] text-black font-black py-2 rounded-lg text-sm hover:bg-[#b8ff3a] disabled:opacity-40 flex items-center justify-center gap-2">
                      {accepting ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>}
                      Aceptar y crear emplazamiento
                    </button>
                    <button onClick={reject} disabled={accepting || rejecting}
                      data-testid="prospect-reject-btn"
                      className="bg-red-500/10 border border-red-500/30 text-red-300 font-black py-2 px-4 rounded-lg text-sm hover:bg-red-500/20 disabled:opacity-40 flex items-center gap-2">
                      {rejecting ? <Loader2 className="animate-spin" size={14}/> : <XCircle size={14}/>}
                      Descartar
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 text-[11px] text-orange-300 bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2">
                    Solo admin o gestor puede aceptar / descartar la prospección. La prospección queda registrada en el buzón para que la valide un gestor.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {showSar && sar && (
        <SARModal sar={sar} onClose={() => setShowSar(false)}/>
      )}
    </div>
  );
};

export default ProspectByAddressModal;
